import { Dialog, DialogContent, Grid, Box, Button } from '@mui/material';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import * as React from 'react';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import { useState } from 'react';
import { styled } from '@mui/material/styles';
import { ProfileSettings } from '../Settings/ProfileSettings';
import { GeneralSettings } from '../Settings/GeneralSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useSignOut } from '../../hooks/useSignOut';
import { UserAvatar } from '../UserAvatar/UserAvatar';
import { useTranslation } from 'react-i18next';
import { MODALS_NS } from './i18n';
import { useUserData } from '../../hooks/auths/useUserData';

interface SettingsModalProps {
  open: boolean;
  onClose?: () => void;
}

const StyledListItem = styled(ListItem)<{ active?: boolean }>(({ theme, active }) => ({
  borderRadius: theme.spacing(1),
  padding: '3px 8px',
  paddingLeft: '24px',
  backgroundColor: active ? theme.palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    cursor: 'pointer',
  },
}));

const HeaderListItem = styled(ListItem)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: '8px 8px',
  paddingTop: '18px',
  paddingBottom: '5px',
}));

const ContentWrapper = styled(Box)(() => ({
  position: 'relative',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));


interface NavigationItem {
  kind?: 'header';
  title: string;
  component?: React.ComponentType<{ onSave?: (page: string, data: any) => void }>;
}

const navigation: NavigationItem[] = [
  {
    kind: 'header',
    title: 'Account',
  },
  {
    title: 'Profile',
    component: ProfileSettings,
  },
  {
    title: 'Settings',
    component: GeneralSettings,
  },
];

export const SettingsModal = ({ open, onClose }: SettingsModalProps) => {
  const { name, email, lastName } = useUserData();
  const { t } = useTranslation(MODALS_NS);
  const handleSignOut = useSignOut(onClose);
  const [selectedPage, setSelectedPage] = useState<string>(
    navigation.find((item) => !item.kind && item.component)?.title || '',
  );

  const handleClose = (event: any, reason: string) => {
    if (reason === 'backdropClick') {
      onClose?.();
    }
  };

  const handlePageSelect = (title: string) => {
    setSelectedPage(title);
  };

  const CurrentPageComponent = navigation.find((item) => item.title === selectedPage)?.component;

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '800px',
          borderRadius: '8px',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ height: '100%' }}>
          <Grid
            item
            xs={3}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgb(41, 45, 48)' : '#F8F8F8',
            }}
          >
            <List sx={{ flex: 1, paddingLeft: '8px' }}>
              <ListItem sx={{ p: '4px 8px' }}>
                <ListItemAvatar sx={{ minWidth: 'auto' }}>
                  <UserAvatar size={32} name={name} lastName={lastName} />
                </ListItemAvatar>
                <ListItemText
                  sx={{ mt: 0, mb: 0, ml: '6px' }}
                  primary={`${name} ${lastName}`}
                  secondary={email}
                />
              </ListItem>
              {navigation.map((item, index) => {
                if (item.kind === 'header') {
                  return (
                    <HeaderListItem key={`${item.title}-${index}`}>
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{
                          color: 'textSecondary',
                        }}
                      />
                    </HeaderListItem>
                  );
                }
                return (
                  <StyledListItem
                    key={`${item.title}-${index}`}
                    active={selectedPage === item.title}
                    onClick={() => handlePageSelect(item.title)}
                  >
                    <ListItemText primary={item.title} />
                  </StyledListItem>
                );
              })}
            </List>
            <Box p={2}>
              <Button
                fullWidth
                startIcon={<LogoutIcon />}
                onClick={handleSignOut}
                color="inherit"
                sx={{ justifyContent: 'flex-start' }}
              >
                {t('settings.signOut')}
              </Button>
            </Box>
          </Grid>
          <Grid
            item
            xs={9}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <ContentWrapper>
              <Box p={4} sx={{ flex: '1 1 0%', overflow: 'auto' }}>
                {CurrentPageComponent && <CurrentPageComponent />}
              </Box>
            </ContentWrapper>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
